import CloseIcon from "@mui/icons-material/Close";
import CssBaseline from "@mui/material/CssBaseline";
import { Theme as MuiTheme, ThemeProvider as MuiThemeProvider, PaletteOptions, StyledEngineProvider, createTheme } from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import { Shadows } from "@mui/material/styles/shadows";
import { ZIndexOptions } from "@mui/material/styles/zIndex";
import globalCss from "designSystem/styles/css";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import React, { FunctionComponent, PropsWithChildren, SyntheticEvent } from "react";
import { SkinVariables, black, blue, boxShadows, charcoal, getSkinVariables, grey, red, white } from "./styles/color";

// element states
export const ACTIVE = "&:active";
export const ACTIVE_MUI = "&.Mui-active";
export const ACTIVE_NOT_SELECTED = "&:active:not(.Mui-selected)";
export const AFTER = "&::after";
export const BEFORE = "&::before";
export const DISABLED = "&.Mui-disabled";
export const FIRST = "&:first-of-type";
export const FOCUS = "&:focus";
export const FOCUSED = "&.Mui-focused";
export const FOCUS_VISIBLE = "&.Mui-focusVisible";
export const HOVER = "&:hover";
export const HOVER_NOT_SELECTED = "&:hover:not(.Mui-selected)";
export const LAST = "&:last-child";
export const PLACEHOLDER = "&::placeholder";
export const SELECT = "& .MuiSelect-select";
export const SELECTED = "&.Mui-selected";
export const SPACING = 4;
export const TEXTFIELD_INNER_OUTLINE = 0;

export const DEFAULT_LINE_HEIGHT = "14px";
export const TEXT_FIELD_ERROR_TOP_POSITION = "24px";

export const FONT_BOLD = 700;
export const FONT_SEMIBOLD = 600;
export const FONT_NORMAL = 400;

export const MODAL_Z_INDEX = 800;

/**
 * Fix [Types] Property "palette", "spacing" does not exist on type 'DefaultTheme'
 * To fix this, you need to augment the DefaultTheme (empty object) in @mui/material/styles with Theme from the core.
 * https://mui.com/material-ui/migration/troubleshooting/#types-property-quot-palette-quot-quot-spacing-quot-does-not-exist-on-type-defaulttheme
 */
declare module "@mui/material/styles" {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	interface DefaultTheme extends MuiTheme { }
}

declare module "@mui/material/styles/createPalette" {
	interface Palette extends SkinVariables {
	}
	interface PaletteOptions extends SkinVariables {
	}
}

function stopPropagation(event: SyntheticEvent): void {
	event.stopPropagation();
}

function getComponents(palette: PaletteOptions): Record<string, any> {
	const {
		FocusPrimary,
		FocusSecondary,
		HighlightSelected,
		LinkDefault,
		LinkHover,
		PrimaryDefault,
		PrimaryDisabledBG,
		PrimaryHoverPressedBG,
		SelectedTabText,
	} = palette;

	const backgroundPaper = palette.background.paper;
	const focusBoxShadow = `0 0 0px 2px ${FocusPrimary}`;

	return {
		MuiAutocomplete: {
			defaultProps: {
				ChipProps: {
					deleteIcon: (<CloseIcon />),
				},
			},
			styleOverrides: {
				clearIndicator: {
					backgroundColor: grey[200],
					borderColor: grey[400],
					borderStyle: "solid",
					borderRadius: "9px",
					borderWidth: "1px",
					color: grey.A100,
					height: "18px",
					marginBottom: "1px", // has to be explicit to override default styling
					marginLeft: "1px",
					marginRight: "1px",
					marginTop: "1px",
					padding: 0,
					visibility: "visible",
					width: "18px",
					"& svg": {
						height: "16px",
						width: "16px",
					},
					[HOVER]: {
						backgroundColor: grey[400],
						color: grey.A100,
					},
					[ACTIVE]: {
						backgroundColor: grey[400],
						color: black[400],
					},
				},
				groupUl: {
					"& $option": {
						paddingLeft: 8,
					},
				},
				input: {
					maxWidth: "calc(100% - 12px)",
				},
				inputRoot: {
					paddingLeft: 4,
					"&.MuiInput-root, &.MuiOutlinedInput-root": {
						paddingBottom: 0,
						paddingTop: 0,
						// Mui autocomplete uses absolute positioning for the end adornment, and various amounts
						// of padding to make room for it.  Overriding this.
						// removing :"!important" as of C8-37009. Re-Tested C8-35433 seems to be ok
						paddingRight: "0",
						"& $input": {
							padding: "4px",
						},
						"& $input:first-of-type": {
							padding: "4px 0",
						},
						".MuiOutlinedInput-input": {
							padding: 0,
						},
					},
					borderColor: grey[400],
				},
				listbox: {
					padding: "4px 0",
					ul: {
						marginBlockEnd: 0,
						marginBlockStart: 0,
					},
				},
				noOptions: {
					padding: "8px 16px",
				},
				option: {
					"&.MuiAutocomplete-option": {
						"--AutoCompleteBackgroundColor": white[50],

						// use !important since base MUI component uses background color for other states while we just specify variable there
						backgroundColor: "var(--AutoCompleteBackgroundColor) !important",
						display: "block",
						overflow: "hidden",
						paddingLeft: "8px",
						paddingRight: "8px",
						paddingTop: "6px",
						paddingBottom: "6px",

						[FOCUSED]: {
							"--AutoCompleteBackgroundColor": grey[200],
						},
						"&[aria-selected=\"true\"]": {
							"&.MuiAutocomplete-option": {
								"--AutoCompleteBackgroundColor": HighlightSelected,

								[HOVER]: {
									"--AutoCompleteBackgroundColor": grey[200],
								},
							},
						},
						[ACTIVE]: {
							"--AutoCompleteBackgroundColor": HighlightSelected,
						},
						[FOCUS_VISIBLE]: {
							"--AutoCompleteBackgroundColor": `${grey[200]} !important`,
						},
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",

						"> .MuiBox-root": {
							paddingRight: "20px",
						},

						"&.autocomplete-option-multiselect": {
							paddingBottom: "1px",
							paddingLeft: "4px",
							paddingTop: "1px",
						},
						"&.autocomplete-option-none": {
							backgroundColor: `${white[50]} !important`,
							borderBottom: STANDARD_BORDER_STYLE,
							marginBottom: "4px",
							paddingBottom: "4px",
							paddingLeft: 0,
							paddingRight: 0,
							paddingTop: 0,

							"& > :first-of-type": {
								backgroundColor: "var(--AutoCompleteBackgroundColor)",
								paddingBottom: "1px",
								paddingLeft: "4px",
								paddingRight: "8px",
								paddingTop: "1px",
							},
						},
						"&.autocomplete-option-select-all": {
							backgroundColor: `${white[50]} !important`,
							borderBottom: STANDARD_BORDER_STYLE,
							marginBottom: "4px",
							paddingBottom: "4px",
							paddingLeft: "4px",
							paddingRight: 0,
							paddingTop: 0,
						},
						"&.autocomplete-option-none-highlight": {
							"--AutoCompleteBackgroundColor": HighlightSelected,
						},
						"&.autocomplete-singleselect-option-none": {
							borderTop: STANDARD_BORDER_STYLE,
							"&.area-selected": {
								"--AutoCompleteBackgroundColor": HighlightSelected,
							},
							"&div": {
								padding: "6px 8px !important",
							},
						},
						"&.autocomplete-option-slim": {
							alignItems: "flex-start",
							display: "flex",
							flexDirection: "column",
							gap: "4px",
							paddingBottom: "8px",
							paddingTop: "8px",

							"& > :last-child": {
								color: grey.A100,
							},

							"&.autocomplete-option-multiselect": {
								gap: 0,
								paddingBottom: "6px",
								paddingTop: "6px",

								"& > :last-child": {
									width: "100%",

									// extra padding around checkbox makes gap more than desired 4px, so move second row of text back up a bit
									marginTop: "-2px",
								},
							},
						},
					},
				},
				paper: {
					borderColor: grey[400],
					borderStyle: "solid",
					borderWidth: 1,
					boxShadow: boxShadows.popper,
					margin: 0,
				},
				popper: {
					// this value is zIndex for single and multiSelect dropdowns
					// this value should be greater then any popup dialog zIndex
					zIndex: 9999,
				},
				popupIndicator: {
					color: black[100],
					marginRight: 0,
				},
				popupIndicatorOpen: {
					transform: "initial",
				},
				root: {
					"&.MuiAutocomplete-hasPopupIcon": {
						"& .MuiAutocomplete-inputRoot": {
							paddingRight: 20,
						},
						"&.MuiAutocomplete-hasClearIcon": {
							"& .MuiAutocomplete-inputRoot": {
								paddingRight: 56,
							},
						},
					},
				},
				tag: {
					margin: 2,
					maxWidth: "100%",
					marginRight: 4,
				},
				endAdornment: {
					"&.MuiAutocomplete-endAdornment.MuiAutocomplete-endAdornment.MuiAutocomplete-endAdornment": {
						right: 0,
					},
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					backgroundColor: white[50],
					borderColor: grey[400],
					borderLeftWidth: 0,
					borderRightWidth: 0,
					borderStyle: "solid",
					borderBottomWidth: 1,
					borderTopWidth: 1,

					"&.Mui-expanded:before": {
						opacity: "1 !important",
					},
					"&:not(:last-child)": {
						borderBottomWidth: 0,
					},
					// the second element is the first attribute
					"&:nth-of-type(2)": {
						borderTopWidth: 0,
					},
					"&:before": {
						display: "none",
					},
					"&.Mui-expanded": {
						margin: "auto",
					},
				},
			},
		},
		MuiAccordionDetails: {
			styleOverrides: {
				root: {
					flexDirection: "column",
					paddingLeft: 0,
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				content: {
					fontWeight: "bold",
					margin: "8px 0",
					"&.Mui-expanded": {
						margin: "8px 0",
					},
				},
				root: {
					marginBottom: -1,
					minHeight: 36,
					paddingLeft: 0,
					paddingRight: "20px !important",
					"&.Mui-expanded": {
						minHeight: 36,
						borderBottomWidth: 0,
					},
				},
			},
		},
		MuiAvatar: {
			styleOverrides: {
				colorDefault: {
					color: blue[900],
					backgroundColor: blue[200],
				},
				root: {
					display: "inline-flex",
					height: 24,
					width: 24,
				},
			},
		},
		MuiBackdrop: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(0, 0, 0, 0.4)",

					".MuiPopover-root &": {
						backgroundColor: "rgba(0,0,0,0)",
					},
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				contained: {
					boxShadow: null,
				},

				endIcon: {
					marginLeft: 4,
					marginRight: "0 !important", // important used to override left margin in any icon size,
				},

				iconSizeMedium: {
					"& > *:first-of-type": {
						fontSize: 16,
					},
				},

				iconSizeSmall: {
					"& > *:first-of-type": {
						fontSize: 16,
					},
				},

				root: {
					borderStyle: "solid",
					borderWidth: 1,
					color: black[100],
					fontSize: 12,
					fontWeight: FONT_NORMAL,
					height: 26,
					lineHeight: DEFAULT_LINE_HEIGHT,
					minWidth: "auto",
					textTransform: null,
				},

				startIcon: {
					marginLeft: "0 !important", // important used to override left margin in any icon size
					marginRight: 4,
				},

				text: {
					padding: "4px 10px",
				},

				textSizeSmall: {
					fontSize: null,
					height: 20,
					padding: 2,
				},
			},
		},
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true,
			},
		},
		MuiButtonGroup: {
			styleOverrides: {
				root: {
					"& .MuiButtonGroup-grouped:not(:last-of-type)": {
						borderColor: grey[400],
						borderRightWidth: 0,
					},
					"& .MuiButtonGroup-grouped:not(:last-of-type).Mui-disabled": {
						borderRightWidth: 0,
					},
				},
			},
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					padding: 12,
					"&:last-child": {
						paddingBottom: 12,
					},
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					// override body2 being used as default typography
					fontSize: 12,
					lineHeight: "unset",
				},
				":focus": {
					outline: "unset",
				},
				"label + .MuiInputBase-formControl": {
					// bottom margin allows for MuiFormHelperText
					marginBottom: 16,
					marginTop: 22,

					paddingLeft: 4,
				},
				...globalCss,
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					backgroundColor: white[50],
					borderColor: grey[600],
					borderRadius: "3px",
					borderStyle: "solid",
					borderWidth: "1px",
					display: "inline-block",
					padding: 0,
					verticalAlign: "top",

					height: 16,
					width: 16,

					position: "relative",
					left: 4,
					top: 4,

					"&:hover": {
						borderColor: grey[900],
					},
					"&:active": {
						borderColor: blue[600],
					},
					"&.Mui-checked": {
						backgroundColor: blue[600],

						"&:hover": {
							backgroundColor: blue[700],
						},

						"&.Mui-checked.Mui-checked": {
							borderColor: "transparent",
						},
					},
					"&.Mui-disabled.Mui-disabled.Mui-disabled": {
						backgroundColor: grey[100],
						borderColor: grey[400],

						"&.Mui-checked": {
							backgroundColor: blue[300],
							borderColor: "transparent",
						},
					},
					"&:focus-within": {
						boxShadow: focusBoxShadow,
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				clickable: {
					[HOVER]: {
						backgroundColor: grey[600],
					},
					[FOCUS]: {
						boxShadow: focusBoxShadow,
					},
				},
				root: {
					backgroundColor: grey[300],
					borderRadius: "10px",
					height: "20px",
				},
				label: {
					color: black[100],
					fontSize: 12,
					fontWeight: FONT_NORMAL,
					lineHeight: DEFAULT_LINE_HEIGHT,
					paddingBottom: 3,
					paddingLeft: 8,
					paddingRight: 8,
					paddingTop: 3,
				},
				icon: {
					fontSize: 14,
					color: grey.A100,
				},
				deleteIcon: {
					backgroundColor: grey[300],
					borderRadius: 8,
					color: grey.A100,
					height: 16,
					marginLeft: 0,
					marginRight: 2,
					width: 16,

					[HOVER]: {
						backgroundColor: grey[600],
						color: grey.A100,
					},
					[ACTIVE]: {
						backgroundColor: grey[600],
						color: grey.A100,
					},
				},
				outlined: {
					backgroundColor: "transparent",
					borderColor: grey[400],

					"& .MuiChip-deleteIcon": {
						backgroundColor: "unset",
						marginRight: 2,
					},
				},
			},
		},
		MuiDialog: {
			defaultProps: {
				maxWidth: false,
			},
			styleOverrides: {
				paper: {
					boxShadow: boxShadows.dialog,
					minWidth: 250,
					overflowX: "visible",
					overflowY: "visible",
				},
			},
		},
		MuiDialogActions: {
			styleOverrides: {
				root: {
					// add min height in case there are no actions, such as in progress dialog
					minHeight: 32,
					padding: 12,
				},
				spacing: {
					"&&>*": {
						marginLeft: 8,
					},
				},
			},
		},
		MuiDialogContent: {
			styleOverrides: {
				dividers: {
					borderWidth: 0,
					borderTopWidth: 1,
					borderBottomWidth: 1,
					borderStyle: "solid",
					borderColor: grey[200],
					display: "flex",
					overflow: "hidden",
				},
				root: {
					"& > .csi-dojo-view": {
						width: "100%",
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					".csi-date-picker-popper &": {
						left: -5,
						top: 5,

						position: "absolute",

						"& .MuiIconButton-edgeEnd": {
							marginRight: 19,
						},
					},
				},
			},
		},
		MuiCalendarPicker: {
			styleOverrides: {
				root: {
					border: "1px solid",
					borderColor: grey[400],
					borderRadius: 3,
					padding: 0,

					width: 320,

					"&>div:first-of-type": {
						marginBottom: 2,
						marginTop: 11,

						paddingLeft: 16,
					},

					"& [role=presentation]": {
						fontWeight: 700,
					},

					"& MuiIconButton-edgeEnd": {
						marginRight: 16,
					},

					"& .MuiTypography-caption": {
						fontSize: 12,

						fontWeight: 600,
						width: 36,
					},

					"& .PrivatePickersSlideTransition-root": {
						minHeight: "unset",
						paddingBottom: 19,

						"&>div:first-of-type": {
							position: "static",
						},
					},
				},
				viewTransitionContainer: {
					overflowY: "hidden",

					"&>div:first-of-type": {
						"&>div:first-of-type": {
							width: "100%",
						},
					},

					"& [role=cell]": {
						width: 44,
					},
				},
			},
		},
		MuiPickersDay: {
			styleOverrides: {
				root: {
					fontSize: 12,

					margin: "0 4px",

					width: 32,
					height: 32,

					[SELECTED]: {
						borderRadius: 20,
						backgroundColor: PrimaryDefault,
					},

					[HOVER]: {
						borderRadius: 20,

						backgroundColor: blue[600],
						color: white[50],
					},
				},
				today: {
					"&.MuiPickersDay-root": {
						borderRadius: 20,
						borderColor: PrimaryDefault,

						[SELECTED]: {
							backgroundColor: blue[600],
						},
					},

				},
			},
		},
		PrivatePickersSlideTransition: {
			styleOverrides: {
				root: {
					minHeight: 180,
				},
			},
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					alignItems: "center",
					display: "flex",
					fontSize: 14,
					fontWeight: "bold",
					padding: 12,

					// fix the height so that 26px close button does not affect overall height with 12px margins 
					height: 42,
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					marginTop: 4,
					marginBottom: 4,
				},
			},
		},
		MuiFormControlLabel: {
			defaultProps: {
				labelPlacement: "top",
			},
			styleOverrides: {
				root: {
					flexShrink: 0,
					lineHeight: DEFAULT_LINE_HEIGHT,
					marginLeft: 0,
					marginRight: 0,
				},
				asterisk: {
					display: "none",
				},
				label: {
					// in cases where the label is a component, we want to make sure it does not overflow its bounds;
					// otherwise OverflowText inside the component will not work
					overflow: "hidden",

					[DISABLED]: {
						color: `${black[100]} !important`,
					},
				},
				labelPlacementBottom: {
					marginLeft: null,

					"& > div:first-of-type": {
						marginBottom: 4,
					},
				},
				labelPlacementEnd: {
					marginLeft: null,
					marginRight: null,

					"& > div:first-of-type": {
						// the label can shrink, but not the control
						flexShrink: 0,
						marginInlineEnd: 8,
					},
				},
				labelPlacementStart: {
					marginLeft: null,
					marginRight: null,

					"& > div:first-of-type": {
						// the label can shrink, but not the control
						flexShrink: 0,
						marginInlineStart: 8,
					},
				},
				labelPlacementTop: {
					marginLeft: null,

					"& > div:first-of-type": {
						marginTop: 4,
					},
				},
			},
		},
		MuiFormGroup: {
			styleOverrides: {
				root: {
					display: "inline-flex",
					verticalAlign: "middle",

					"&:not(.MuiFormGroup-row) > * + *": {
						marginTop: 8,
					},
				},
				row: {
					"& > * + *": {
						marginLeft: 16,
					},
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					bottom: 0,
					fontSize: "inherit",
					lineHeight: DEFAULT_LINE_HEIGHT,
					margin: 0,
					marginTop: 2,
					position: "absolute",
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					// override default color; "important" needed to override error and focused states
					color: "inherit !important",
					fontWeight: "bold",
					lineHeight: DEFAULT_LINE_HEIGHT,
				},
			},
		},
		MuiIcon: {
			styleOverrides: {
				colorError: {
					color: red[600],
				},
				colorPrimary: {
					color: PrimaryDefault,
				},
				root: {
					color: grey.A100,
					fontSize: 16,
					verticalAlign: "middle",
				},
				fontSizeLarge: {
					fontSize: 20,
				},
				fontSizeSmall: {
					fontSize: 14,
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					padding: 0,

					"& .svg-inline--fa": {
						// button will control cursor rather than icon
						cursor: "initial",
					},
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				positionEnd: {
					marginLeft: 0,
					paddingLeft: 3,
				},
				positionStart: {
					marginRight: 0,
					paddingRight: 3,
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					borderRadius: 3,
					borderStyle: "solid",
					borderWidth: 1,
					fontSize: "inherit",
					lineHeight: DEFAULT_LINE_HEIGHT,
					minHeight: 26,
					padding: 4,

					[DISABLED]: {
						background: grey[100],
						color: black[500],
						pointerEvents: "none",
					},
				},
				multiline: {
					padding: "4px",
				},
				input: {
					height: "auto",
					padding: 0,

					[DISABLED]: {
						color: `${grey[800]} !important`,
						WebkitTextFillColor: "unset",

						[PLACEHOLDER]: {
							color: `${grey[800]} !important`,
						},
					},
					[PLACEHOLDER]: {
						color: grey[800],
						opacity: 1,
					},
				},
			},
		},
		MuiInput: {
			defaultProps: {
				disableUnderline: true,
			},
		},
		MuiInputLabel: {
			defaultProps: {
				disableAnimation: true,
				shrink: true,
			},
			styleOverrides: {
				asterisk: {
					color: red[600],
				},
				shrink: {
					transform: "translate(0, 0)",
				},
			},
		},
		MuiLink: {
			defaultProps: {
				// href is set so that links with just onClick behavior still get tab focus
				href: "#",
				onClick: stopPropagation,
				underline: "none",
			},
			styleOverrides: {
				root: {
					alignItems: "center",
					display: "inline-flex",
					position: "relative",

					[FOCUS_VISIBLE]: {
						[AFTER]: {
							borderRadius: 3,
							boxShadow: focusBoxShadow,
							content: "\"\"",	// won't work in TSX with just double-quotes
							position: "absolute",
							left: -2,
							top: -2,
							height: "calc(100% + 4px)",
							width: "calc(100% + 4px)",
						},
					},
					"&:not(.csi-anchor-reset)": {
						color: LinkDefault,

						[HOVER]: {
							color: LinkHover,
						},

						"& *": {
							color: "currentColor",
						},
					},
				},
			},
		},
		MuiList: {
			defaultProps: {
				disablePadding: true,
			},
		},
		MuiListItem: {
			styleOverrides: {
				dense: {
					paddingBottom: 0,
					paddingTop: 0,
				},
				root: {
					borderColor: "transparent",
				},
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					// Additional selector to increase specificity to overwrite default mui styling
					"&.MuiListItemIcon-root": {
						color: "inherit",
						minWidth: "auto",
					},
				},
			},
		},
		MuiListItemText: {
			styleOverrides: {
				root: {
					marginBottom: 0,
					marginTop: 0,
				},
				dense: {
					marginBottom: 0,
					marginTop: 0,
				},
				multiline: {
					display: "flex",
					flexDirection: "column",
					gap: "4px",
				},
			},
		},
		MuiListSubheader: {
			styleOverrides: {
				gutters: {
					paddingLeft: 8,
				},
				root: {
					lineHeight: "36px",
				},
				sticky: {
					height: 26,

					// would cover the padding when scrolling so that the text won't show up in that padding
					top: -4,
				},
			},
		},
		MuiMenu: {
			styleOverrides: {
				paper: {
					borderColor: grey[400],
					borderStyle: "solid",
					borderWidth: 1,
					boxShadow: boxShadows.popper,
					paddingBottom: 4,
					paddingLeft: 0,
					paddingRight: 0,
					paddingTop: 4,
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					paddingBottom: 5,
					paddingLeft: 8,
					paddingRight: 8,
					paddingTop: 5,
					"&.Mui-selected:focus": {
						backgroundColor: HighlightSelected,
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				input: {
					padding: 0,
				},

				root: {
					padding: 4,
				},
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					"&.Mui-checked": {
						borderColor: blue[600],
					},
					"&:hover": {
						borderColor: grey[900],
					},
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				icon: {
					color: black[100],
					right: 4,
					top: "calc(50% - 8px)",
				},
				iconOpen: {
					transform: "initial",
				},
				select: {
					minHeight: "auto",
					// overrides .MuiSelect-select.MuiSelect-select (no idea why it is double)
					paddingRight: "20px !important",

					[FOCUS]: {
						backgroundColor: "inherit",
						borderRadius: 3,
					},
				},
			},
		},
		MuiSvgIcon: {
			styleOverrides: {
				colorError: {
					color: red[600],
				},
				colorPrimary: {
					color: PrimaryDefault,
				},
				root: {
					color: grey.A100,
					fontSize: 16,
					verticalAlign: "middle",
				},
				fontSizeLarge: {
					fontSize: 20,
				},
				fontSizeSmall: {
					fontSize: 14,
				},
			},
		},
		MuiSwitch: {
			styleOverrides: {
				root: {
					display: "flex",
					height: 20,
					margin: 0,
					padding: 2,
					width: 42,
				},
				switchBase: {
					padding: 3,

					"&.Mui-checked": {
						transform: "translateX(22px)",
					},
					"&.Mui-checked + .MuiSwitch-track": {
						backgroundColor: PrimaryDefault,
						opacity: 1,
					},
					"&:hover + .MuiSwitch-track": {
						backgroundColor: grey[900],
					},
					"&.Mui-checked:hover + .MuiSwitch-track": {
						backgroundColor: PrimaryHoverPressedBG,
						opacity: 1,

					},
					"&:active + .MuiSwitch-track": {
						backgroundColor: grey[900],
					},
					"&.Mui-checked:active + .MuiSwitch-track": {
						backgroundColor: PrimaryHoverPressedBG,
						opacity: 1,
					},
					"&.Mui-disabled + .MuiSwitch-track": {
						backgroundColor: grey[400],
						opacity: 1,
					},
					"&.Mui-checked&.Mui-disabled + .MuiSwitch-track": {
						backgroundColor: PrimaryDisabledBG,
						opacity: 1,
					},
					"&:focus-within + .MuiSwitch-track": {
						boxShadow: focusBoxShadow,
					},
				},
				thumb: {
					width: 14,
					height: 14,
					boxShadow: "none",
					color: "#FFFFFF",
				},
				track: {
					backgroundColor: grey[700],
					borderRadius: 8,
					opacity: 1,
				},
			},
		},
		MuiSlider: {
			styleOverrides: {
				thumb: {
					backgroundColor: white[50],
					border: "1px solid",
					borderColor: blue[600],
					height: 17,
					width: 17,
					[HOVER]: {
						boxShadow: `0px 0px 0px 5px  ${FocusPrimary}66`,
					},
					[FOCUS_VISIBLE]: {
						boxShadow: `0px 0px 0px 5px  ${FocusPrimary}66`,
					},
					[ACTIVE]: {
						boxShadow: `0px 0px 0px 7px  ${FocusPrimary}66`,
					},
					[ACTIVE_MUI]: {
						boxShadow: `0px 0px 0px 7px  ${FocusPrimary}66`,
					},
				},
				track: {
					height: 2,
				},
				rail: {
					borderRadius: 2,
					color: grey[600],
					height: 4,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					color: "inherit",
					display: "inline-flex",
					fontSize: 12,
					fontWeight: "normal",
					lineHeight: "16px",
					marginRight: 16,
					maxWidth: "auto",
					minHeight: "auto",
					minWidth: "auto !important",	// overload media rules
					padding: 0,
					paddingBottom: 6,
					textOverflow: "ellipsis",
					textTransform: "initial",
					whiteSpace: "nowrap",

					[HOVER_NOT_SELECTED]: {
						borderBottomColor: grey[400],
						borderBottomStyle: "solid",
						borderBottomWidth: 3,
						paddingBottom: 3,
					},
					[ACTIVE_NOT_SELECTED]: {
						color: SelectedTabText,
						fontWeight: FONT_SEMIBOLD,
					},
					[SELECTED]: {
						color: SelectedTabText,
						fontWeight: FONT_SEMIBOLD,
					},
					[AFTER]: {
						// hidden pseudo-element added to prevent jitter as tabs are selected and turn bold
						content: "attr(data-csi-tab-name)",
						fontWeight: "bold",
						height: 0,
						pointerEvents: "none",
						userSelect: "none",
						visibility: "hidden",
					},

					".csi-date-picker-popper &": {
						height: 44,

						[SELECTED]: {
							"& .MuiSvgIcon-root": {
								color: blue[600],
								fontSize: 20,
								fontWeight: FONT_SEMIBOLD,
							},
						},

						"& .MuiSvgIcon-root": {
							fontSize: 20,
						},
					},
				},
				textColorInherit: {
					opacity: 1,
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					borderBottomColor: grey[400],
					borderBottomStyle: "solid",
					borderBottomWidth: 1,
					color: charcoal[50],
					flexShrink: 0,
					minHeight: "auto",

					// needed for scroll buttons
					paddingTop: 2,
					position: "relative",
				},
				indicator: {
					backgroundColor: PrimaryDefault,
					height: 3,
				},
				scrollButtons: {
					boxShadow: `-10px 0 2px -2px ${backgroundPaper}`,
					position: "absolute",
					left: 0,
					top: 0,
					zIndex: 1,

					[LAST]: {
						left: "unset",
						right: 0,
					},
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					backgroundColor: backgroundPaper,
					borderColor: grey[400],
					color: grey.A100,
					padding: 4,

					[HOVER]: {
						backgroundColor: grey[100],
						borderColor: grey[700],
						zIndex: 1,
					},
					[FOCUS_VISIBLE]: {
						boxShadow: focusBoxShadow,
						zIndex: 3,
					},
					[SELECTED]: {
						backgroundColor: grey[100],
						boxShadow: boxShadows.activeButton,
						color: grey.A100,
						zIndex: 2,

						[FOCUS_VISIBLE]: {
							boxShadow: `${boxShadows.activeButton}, ${focusBoxShadow}`,
							zIndex: 3,
						},
						[HOVER]: {
							// for some reason, important is needed here to override MUI
							backgroundColor: `${grey[100]} !important`,
							borderColor: grey[700],
						},
						"& + .Mui-selected:active": {
							// override default MUI from hiding left border if two siblings selected
							borderLeft: `1px solid ${FocusSecondary} !important`,
							marginLeft: "-1px !important",
						},

						[SELECTED]: {
							// override default MUI from hiding left border
							[SELECTED]: {
								borderColor: grey[700],
							},
						},
					},
					[ACTIVE]: {
						borderColor: `${FocusSecondary} !important`,
						zIndex: 4,

						"& + .MuiToggleButtonGroup-groupedHorizontal": {
							borderLeftColor: FocusSecondary,
						},
					},
					[ACTIVE_NOT_SELECTED]: {
						boxShadow: boxShadows.activeButton,
					},
					[DISABLED]: {
						color: "rgba(0, 0, 0, 0.12)",
					},
				},
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				groupedHorizontal: {
					"&:not(:first-of-type)": {
						borderLeftColor: grey[400],
					},
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: black[400],
					fontSize: 10,
					padding: "4px 8px",
					"& .csi-no-tooltip-colors": {
						color: "unset",
					},

					"&&.MuiTooltip-tooltipPlacementLeft": {
						marginRight: 8,
					},
					"&&.MuiTooltip-tooltipPlacementRight": {
						marginLeft: 8,
					},
					"&&.MuiTooltip-tooltipPlacementTop": {
						marginBottom: 8,
					},
					"&&.MuiTooltip-tooltipPlacementBottom": {
						marginTop: 8,
					},
				},
				arrow: {
					color: black[400],
				},
			},
		},
		MuiTreeItem: {
			styleOverrides: {
				content: {
					cursor: "auto",
					marginBottom: 1,
					"& img": {
						cursor: "pointer",
					},
					"&&": {
						backgroundColor: "inherit",
					},
				},
				group: {
					"&&": {
						borderLeft: "none",
					},
				},
				iconContainer: {
					cursor: "pointer",
				},
				label: {
					fontSize: "12px",
					paddingLeft: 0,
					width: "100%",
					display: "flex",
				},
			},
		},
		MuiTreeView: {
			styleOverrides: {
				root: {
					paddingRight: 12,
				},
			},
		},
		MuiPopover: {
			defaultProps: {
				marginThreshold: 12,
			},
			styleOverrides: {
				paper: {
					borderColor: grey[400],
					borderStyle: "solid",
					borderWidth: 1,
					boxShadow: boxShadows.popper,
				},
				root: {
					// this is latest default value for modals in MUI and lies above both Dojo and React dialogs;
					// we currently have React dialogs at 800 though, but Popovers should appear above most anything
					zIndex: 1300,
				},
			},
		},
	};
};

const SHAPE = {
	borderRadius: 3,
};

const SHADOWS: Shadows = [
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
	"none",
];

const TYPOGRAPHY: TypographyOptions = {
	fontFamily: "Centric8, Centric8SimSun",
	fontSize: 12,
	fontWeightMedium: FONT_SEMIBOLD,
	htmlFontSize: 12,
	h1: {
		fontSize: 24,
		fontWeight: FONT_SEMIBOLD,
	},
	h2: {
		fontSize: 20,
		fontWeight: FONT_NORMAL,
	},
	h3: {
		fontSize: 18,
		fontWeight: FONT_BOLD,
	},
	h4: {
		fontSize: 16,
		fontWeight: FONT_BOLD,
	},
	h5: {
		fontSize: 14,
		fontWeight: FONT_SEMIBOLD,
	},
	h6: {
		fontSize: 12,
		fontWeight: FONT_BOLD,
		lineHeight: DEFAULT_LINE_HEIGHT,
	},
	subtitle1: {
		fontSize: 12,
		fontWeight: FONT_SEMIBOLD,
		lineHeight: "16px",
	},
	subtitle2: {
		fontSize: 10,
		fontWeight: FONT_SEMIBOLD,
		lineHeight: DEFAULT_LINE_HEIGHT,
	},
	body1: {
		fontSize: 12,
		lineHeight: DEFAULT_LINE_HEIGHT,
	},
	body2: {
		fontSize: 10,
		lineHeight: "12px",
	},
	button: {
		fontSize: 12,
		textTransform: "unset",
	},
	caption: {
		fontSize: 9,
		lineHeight: "unset",
	},
	overline: {
		fontSize: 10,
		lineHeight: "unset",
		textTransform: "uppercase",
	},
};

const ZINDEX: ZIndexOptions = {
	// mobileStepper: 1000,
	// speedDial: 1050,
	// appBar: 1100,
	// drawer: 1200,
	modal: MODAL_Z_INDEX,
	// snackbar: 1400,
	tooltip: 20000,
};

/**
 * Create MUI theme palette based on given skin name
 */
function getSkinPalette(skin: string): PaletteOptions {
	const vars = getSkinVariables(skin);

	// NOTE: currently there is nothing skin-specific in the palette other than
	// the variables, but if we introduce a dark theme, then I suspect that may change
	// (at least for the text colors below

	return {
		action: {
			active: grey.A100,

			// disable default action opacity except for disabled
			activatedOpacity: 0,
			disabled: "rgba(0, 0, 0, 0.65)",
			disabledOpacity: 0.65,
			focusOpacity: 0,
			hoverOpacity: 0,
			selectedOpacity: 0,

		},
		background: {
			default: white[50],
			paper: white[50],
		},
		error: {
			main: red[600],
		},
		grey,
		primary: {
			main: vars.PrimaryDefault,
		},
		text: {
			primary: black[100],   // PrimaryText, PrimaryBlack
			secondary: black[500], // SecondaryText
		},
		...vars,
	};
}

interface ThemeProviderProps extends PropsWithChildren {
	/** skin that drives the palette for theme (defaults to CentricBlue) */
	skin?: string;
}

const Theme: FunctionComponent<ThemeProviderProps> = (props) => {
	const { children, skin, ...otherProps } = props;
	const palette = getSkinPalette(skin || "CentricBlue");
	const components = getComponents(palette);
	const theme = createTheme({
		components,
		palette,
		shadows: SHADOWS,
		shape: SHAPE,
		spacing: SPACING,
		typography: TYPOGRAPHY,
		zIndex: ZINDEX,
	});

	return (
		<StyledEngineProvider injectFirst>
			<MuiThemeProvider theme={theme} {...otherProps}>
				<CssBaseline />
				{children}
			</MuiThemeProvider>
		</StyledEngineProvider>
	);
};

export default Theme;
